import {
  Flex,
  Select,
  Table,
  Tooltip,
} from 'antd'
import Paragraph from 'antd/es/typography/Paragraph'
import dayjs from 'dayjs'
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react'
import useAsync from 'react-use/lib/useAsync'

import { ReportsPageProps } from '@/components/feature/analytics/hooks/use-report-search'
import useScrollToBottom from '@/components/feature/analytics/hooks/use-scroll-to-bottom'
import { EarningType } from '@/constants/earning-type'
import { PaginationQuery } from '@/constants/pagination'
import {
  TopFansOrder,
  topFansOrderOptions,
} from '@/constants/top-fans-order'
import { readTopFans } from '@/redux/fans-reports'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'
import { formatPrice } from '@/utils/formatter'

import SectionTitle from '../section-title'

export interface FansReportsTopFansProps extends ReportsPageProps {
  creatorId: number
  containerRef: React.RefObject<HTMLDivElement>
}

const columns = [
  {
    title: 'Fan name', dataIndex: 'name', key: 'name',
    ellipsis: {
      showTitle: false,
    },
    render(value: string) {
      return (
        <Tooltip title={value} placement="topLeft">
          <Paragraph ellipsis={{ rows: 1 }}>{value}</Paragraph>
        </Tooltip>
      )
    },
  },
  {
    title: 'Total spend', dataIndex: 'totalSpend', key: 'totalSpend',
    render: (value: number) => formatPrice()(value),
  },
  {
    title: 'Subscription', dataIndex: 'subscription', key: 'subscription',
    render: (value: number) => formatPrice()(value),
  },
  {
    title: 'Posts', dataIndex: 'posts', key: 'posts',
    render: (value: number) => formatPrice()(value),
  },
  {
    title: 'Tips', dataIndex: 'tips', key: 'tips',
    render: (value: number) => formatPrice()(value),
  },
  {
    title: 'Messages', dataIndex: 'messages', key: 'messages',
    render: (value: number) => formatPrice()(value),
  },
  {
    title: 'Subscription length', dataIndex: 'subscriptionDays', key: 'subscriptionDays',
    render: (value: number) => {
      return dayjs.duration(value, 'days').humanize()
    },
  },
]

export default function FansReportsTopFans({
  searchValues,
  creatorId,
  containerRef,
}: FansReportsTopFansProps) {
  const { topFans } = useAppSelector(state => state.fansReports)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<TopFansOrder>(TopFansOrder.TotalSpend)
  const [pageQuery, setPagingQuery] = useState<PaginationQuery>({ pageNum: 1, pageSize: 10 })

  const { earningType = EarningType.GrossEarnings, startAt, endAt } = searchValues

  useScrollToBottom(containerRef, () => {
    if (loading || data.rows.length >= data.total) return
    void loadTopFans(true)
  })

  const loadTopFans = useCallback(async (loadMore: boolean = false) => {
    setLoading(true)
    const nextPagingQuery = loadMore ?
      { ...pageQuery, pageNum: (pageQuery.pageNum || 0) + 1 } :
      { pageNum: 1, pageSize: 10 }

    await dispatch(readTopFans({
      startAt,
      endAt,
      creatorId,
      order,
      ...nextPagingQuery,
    })).unwrap()

    setPagingQuery(nextPagingQuery)
    setLoading(false)
  }, [dispatch, creatorId, startAt, endAt, order, pageQuery])

  useAsync(async () => {
    await loadTopFans()
  }, [creatorId, searchValues, order])

  const data = topFans[earningType]

  const table = useMemo(() => {
    return <Table
      rowKey="name"
      loading={loading}
      columns={columns}
      dataSource={data.rows}
      pagination={false}
    />
  }, [data.rows, loading])

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between">
        <SectionTitle
          title="Top fans"
          tooltip="Your Creator's highest-spending fans"
        />
        <Select
          style={{ width: 200 }}
          size="small"
          defaultValue={TopFansOrder.TotalSpend}
          options={topFansOrderOptions}
          onChange={setOrder}
        />
      </Flex>
      <Flex vertical>
        {table}
      </Flex>
    </Flex>
  )
}
